import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanDeactivate,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  selectMasterAccount,
  selectSubAccountId
} from '../../account-info-header/reducers';
import { StandingAuthorizationsComponent } from '../../standing-authorizations/container/standing-authorizations.component';
import { displayCancelModal } from '../actions/shared.actions';
import { selectCancelModalContinueClicked, selectViewInContext } from '../reducers';
import { navRouteConstants, pageInContext } from '../utilities/Constants';
import { CreateRequestComponent } from '../../create-request/container/create-request.component';
import * as fromStandingAuthorizations from '../../standing-authorizations/reducers';
import { selectTransactionType } from '../../create-request/reducers';
import { InitiateAndReleaseComponent } from "projects/money-movement-ui/src/app/initiate-and-release/container/initiate-and-release.component";
import { selectInitReleaseTransactionInContext } from '../../initiate-and-release/reducers';

@Injectable({
  providedIn: 'root',
})
export class RouteGuard
  implements
  CanActivate,
  CanDeactivate<StandingAuthorizationsComponent | CreateRequestComponent | InitiateAndReleaseComponent> {
  accountInContext: boolean = false;
  masterAccountInContext: boolean = false;
  displayCancelModal: boolean = false;
  destroy$ = new Subject<void>();
  step: string;
  isInRTransactionInContext: boolean = false;
  isReviewingInRDraft: boolean = false;

  constructor(private store: Store,
    private router: Router) {
    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$), map((id) => !!id)).subscribe((subAccountPresent) => {
      this.masterAccountInContext = subAccountPresent
    });

    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$), map((id) => !!id)).subscribe((subAccountPresent) => {
      this.accountInContext = subAccountPresent
    });

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe((step) => {
      this.step = step;
    });

    this.store.select(selectInitReleaseTransactionInContext).pipe(takeUntil(this.destroy$)).subscribe((draft) => {
      this.isInRTransactionInContext = !!draft?.draftId;
    });

    combineLatest([
      this.store.select(fromStandingAuthorizations.selectTransactionType),
      this.store.select(selectTransactionType),
      this.store.select(selectCancelModalContinueClicked),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([standingAuthTransactionType, transactionType, continueClicked]) => {
        this.displayCancelModal = (!!transactionType || !!standingAuthTransactionType) && !continueClicked;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  canActivate(route: ActivatedRouteSnapshot): boolean | Observable<boolean> | UrlTree {
    return this.canActivateRoute(route.routeConfig?.path ?? '') ? true : this.router.createUrlTree([navRouteConstants.createTransaction]);
  }

  canDeactivate(
    component:
      | StandingAuthorizationsComponent
      | CreateRequestComponent
      | InitiateAndReleaseComponent,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): boolean {
    this.isReviewingInRDraft = this.step === pageInContext.review && this.isInRTransactionInContext;
    
    if (this.step !== pageInContext.confirmation && this.displayCancelModal  && !this.isReviewingInRDraft) {
      if (currentState.url.includes(`/${navRouteConstants.standingAuth}`)) {
        this.store.dispatch(displayCancelModal({
          nextUrlState: nextState.url,
          title: 'Cancel this work in progress?',
          content: ['The information you have entered to create this standing authorization will be lost.'],
          greenButton: 'Cancel standing authorization',
          grayButton: 'Back',
          source: 'moveMoneyNavBarRedirect'
        }));
        return false;
      } else if (currentState.url.includes(`/${navRouteConstants.createTransaction}`) || currentState.url.includes(`/${navRouteConstants.initiateAndRelease}`)) {
        this.store.dispatch(displayCancelModal({
          nextUrlState: nextState.url,
          title: 'Cancel transaction in progress',
          content: ['Canceling will reset your transaction inputs. Are you sure you want to continue?'],
          greenButton: 'Cancel transaction',
          grayButton: 'Back',
          source: 'moveMoneyNavBarRedirect'
        }));
        return false;
      }
    }
    return true;
  }

  private canActivateRoute(path: string): boolean {
    return path === navRouteConstants.createTransaction || path === navRouteConstants.standingAuth || path === navRouteConstants.setupEAuth || path === navRouteConstants.initiateAndRelease;
  }

  private isRouteInitReleaseTab(path: string) {
    return path === navRouteConstants.initiateAndRelease;
  }

  private isRouteCreateTransactionTab(path: string) {
    return path === navRouteConstants.createTransaction;
  }

}
